import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getProfile, saveProfile, generateAIDescription, getSubscriptionStatus, deleteOwnAccount } from '../services/api';
import './Profile.css';

function calculateAge(dob) {
  if (!dob) return '';
  const today = new Date();
  const birthDate = new Date(dob);
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }
  return age;
}

function Profile() {
  const navigate = useNavigate();
  const username = localStorage.getItem('username');

  const [profileData, setProfileData] = useState({
    username: username,
    maleName: '', maleAge: '', femaleName: '', femaleAge: '',
    description: '', profilePicture: '', aiGeneratedDescription: '',
    maleDob: '', femaleDob: ''
  });

  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [imagePreview, setImagePreview] = useState(null);
  const [subscription, setSubscription] = useState(null);

  // ✅ NEW: Delete account state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');

  useEffect(() => {
    if (!username) { navigate('/login'); return; }
    fetchProfile();
    fetchSubscription();
  }, [username, navigate]);

  const fetchSubscription = async () => {
    try {
      const data = await getSubscriptionStatus(username);
      setSubscription(data);
    } catch (error) {
      console.error('Error fetching subscription:', error);
    }
  };

  const fetchProfile = async () => {
    try {
      setLoading(true);
      const data = await getProfile(username);
      if (data.exists === false) {
        setIsEditing(true);
      } else {
        // Auto-recalculate ages from DOB on fetch
        const updatedData = { ...data };
        if (data.maleDob) updatedData.maleAge = calculateAge(data.maleDob);
        if (data.femaleDob) updatedData.femaleAge = calculateAge(data.femaleDob);
        setProfileData(updatedData);
        if (data.profilePicture) setImagePreview(data.profilePicture);
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
      setIsEditing(true);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setProfileData({ ...profileData, [name]: value });
    setMessage({ type: '', text: '' });
  };

  const handleDobChange = (e) => {
    const { name, value } = e.target;
    const ageField = name === 'maleDob' ? 'maleAge' : 'femaleAge';
    const age = calculateAge(value);
    setProfileData({ ...profileData, [name]: value, [ageField]: age });
    setMessage({ type: '', text: '' });
  };

  const compressImage = (base64String, maxWidth = 400, quality = 0.6) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        let width = img.width;
        let height = img.height;
        if (width > height) {
          if (width > maxWidth) { height = (height * maxWidth) / width; width = maxWidth; }
        } else {
          if (height > maxWidth) { width = (width * maxWidth) / height; height = maxWidth; }
        }
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);
        resolve(canvas.toDataURL('image/jpeg', quality));
      };
      img.onerror = reject;
      img.src = base64String;
    });
  };

  const handleImageChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { setMessage({ type: 'error', text: 'Original image size should be less than 10MB' }); return; }
    if (!file.type.startsWith('image/')) { setMessage({ type: 'error', text: 'Please upload an image file (JPG, PNG, etc.)' }); return; }

    try {
      setMessage({ type: 'info', text: 'Processing image...' });
      const reader = new FileReader();
      reader.onloadend = async () => {
        try {
          const compressedImage = await compressImage(reader.result);
          const sizeInBytes = (compressedImage.length * 3) / 4;
          const sizeInMB = sizeInBytes / (1024 * 1024);

          if (sizeInMB > 5) {
            const moreCompressed = await compressImage(reader.result, 300, 0.5);
            const newSize = ((moreCompressed.length * 3) / 4) / (1024 * 1024);
            if (newSize > 5) { setMessage({ type: 'error', text: 'Image is still too large. Please use a smaller image.' }); return; }
            setImagePreview(moreCompressed);
            setProfileData({ ...profileData, profilePicture: moreCompressed });
            setMessage({ type: 'success', text: `Image compressed to ${newSize.toFixed(2)} MB` });
          } else {
            setImagePreview(compressedImage);
            setProfileData({ ...profileData, profilePicture: compressedImage });
            setMessage({ type: 'success', text: `Image compressed to ${sizeInMB.toFixed(2)} MB` });
          }
          setTimeout(() => setMessage({ type: '', text: '' }), 3000);
        } catch (error) {
          setMessage({ type: 'error', text: 'Failed to process image' });
        }
      };
      reader.onerror = () => setMessage({ type: 'error', text: 'Failed to read image file' });
      reader.readAsDataURL(file);
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to process image' });
    }
  };

  const handleRemoveImage = () => {
    setImagePreview(null);
    setProfileData({ ...profileData, profilePicture: '' });
    const fileInput = document.getElementById('profilePicture');
    if (fileInput) fileInput.value = '';
    setMessage({ type: 'info', text: 'Profile picture removed' });
    setTimeout(() => setMessage({ type: '', text: '' }), 2000);
  };

  const handleSave = async () => {
    setMessage({ type: '', text: '' });
    if (!profileData.maleName?.trim()) { setMessage({ type: 'error', text: 'Male partner name is required' }); return; }
    if (!profileData.femaleName?.trim()) { setMessage({ type: 'error', text: 'Female partner name is required' }); return; }
    if (!profileData.maleAge || profileData.maleAge < 18 || profileData.maleAge > 120) { setMessage({ type: 'error', text: 'Male partner age must be between 18 and 120' }); return; }
    if (!profileData.femaleAge || profileData.femaleAge < 18 || profileData.femaleAge > 120) { setMessage({ type: 'error', text: 'Female partner age must be between 18 and 120' }); return; }

    try {
      setSaving(true);
      const dataToSend = {
        username: profileData.username,
        maleName: profileData.maleName.trim(),
        maleAge: parseInt(profileData.maleAge),
        femaleName: profileData.femaleName.trim(),
        femaleAge: parseInt(profileData.femaleAge),
        description: profileData.description ? profileData.description.trim() : null,
        profilePicture: profileData.profilePicture || null,
        aiGeneratedDescription: profileData.aiGeneratedDescription || null,
        maleDob: profileData.maleDob || null,
        femaleDob: profileData.femaleDob || null
      };
      const savedProfile = await saveProfile(dataToSend);
      setProfileData(savedProfile);
      setIsEditing(false);
      setMessage({ type: 'success', text: 'Profile saved successfully! ✨' });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } catch (error) {
      console.error('Save profile error:', error);
      let errorMessage = 'Failed to save profile. ';
      if (error.response?.data?.message) errorMessage += error.response.data.message;
      else if (error.message) errorMessage += error.message;
      else errorMessage += 'Please try removing the profile picture and try again.';
      setMessage({ type: 'error', text: errorMessage });
    } finally {
      setSaving(false);
    }
  };

  const handleGenerateAI = async () => {
    // Block Free users from generating AI description
    if (!subscription?.isPremium) {
      setMessage({ type: 'error', text: '⭐ AI-generated descriptions are a Premium feature. Upgrade to Premium to unlock.' });
      setTimeout(() => navigate('/plus'), 3000);
      return;
    }

    try {
      setSaving(true);
      const response = await generateAIDescription(username);
      if (response.success) {
        setProfileData({ ...profileData, aiGeneratedDescription: response.description });
        setMessage({ type: 'success', text: 'AI description generated! ✨' });
        setTimeout(() => setMessage({ type: '', text: '' }), 3000);
      }
    } catch (error) {
      console.error('Generate AI error:', error);
      setMessage({ type: 'error', text: 'Failed to generate AI description. Please save your profile first.' });
    } finally {
      setSaving(false);
    }
  };

  // ✅ NEW: Delete account handlers
  const handleOpenDeleteModal = () => {
    setDeleteConfirmText('');
    setDeleteError('');
    setShowDeleteModal(true);
  };

  const handleCloseDeleteModal = () => {
    if (deleting) return;
    setShowDeleteModal(false);
    setDeleteConfirmText('');
    setDeleteError('');
  };

  const handleConfirmDelete = async () => {
    setDeleteError('');
    if (deleteConfirmText.trim() !== username) {
      setDeleteError('The username you typed does not match. Please try again.');
      return;
    }

    try {
      setDeleting(true);
      await deleteOwnAccount(username);
      // Clear all local storage and redirect
      localStorage.clear();
      navigate('/signup');
    } catch (error) {
      console.error('Delete account error:', error);
      setDeleteError(error?.message || 'Failed to delete account. Please try again.');
      setDeleting(false);
    }
  };

  const isProfileComplete = () => profileData.maleName && profileData.femaleName && profileData.maleAge && profileData.femaleAge;

  const renderSubscriptionStatus = () => {
    if (!subscription) return null;
    const isPremium = subscription.isPremium;
    const endDate = subscription.endDate;

    return (
      <div className={`subscription-status-card ${isPremium ? 'premium' : 'free'}`}>
        <div className="subscription-status-header">
          <span className="subscription-icon">{isPremium ? '⭐' : '🆓'}</span>
          <div>
            <h4 className="subscription-plan-name">{isPremium ? 'Premium Plan' : 'Free Plan'}</h4>
            {isPremium && endDate && <p className="subscription-expiry">Active until {endDate}</p>}
            {!isPremium && <p className="subscription-expiry">Limited features</p>}
          </div>
        </div>
        {!isPremium && (
          <button className="subscription-upgrade-btn" onClick={() => navigate('/plus')}>
            Upgrade to Premium →
          </button>
        )}
        {isPremium && (
          <button className="subscription-manage-btn" onClick={() => navigate('/plus')}>
            Manage Plan →
          </button>
        )}
      </div>
    );
  };

  if (loading) {
    return <div className="profile-container"><div className="loading-spinner">Loading profile...</div></div>;
  }

  const today = new Date().toISOString().split('T')[0];
  const minDob = '1900-01-01';

  return (
    <div className="profile-container">
      <div className="profile-header">
        <h1>👫 Couple Profile</h1>
        <p>Manage your relationship profile</p>
        {isProfileComplete() && !isEditing && <div className="profile-complete-badge">✅ Profile Complete</div>}
      </div>

      {message.text && (
        <div className={`message ${message.type}`}>
          {message.type === 'success' && '✅'}
          {message.type === 'error' && '⚠️'}
          {message.type === 'info' && 'ℹ️'}
          {' '}{message.text}
        </div>
      )}

      {renderSubscriptionStatus()}

      <div className="profile-content">
        <div className="profile-picture-section">
          <h3>Profile Picture <span className="optional-label">(Optional)</span></h3>
          <p className="section-note">Images are automatically compressed to optimize storage. Recommended: Use images under 5MB for best results.</p>
          <div className="picture-upload">
            <div className="picture-preview">
              {imagePreview ? (
                <img src={imagePreview} alt="Profile" />
              ) : (
                <div className="picture-placeholder">
                  <span>📷</span>
                  <p>No image yet</p>
                </div>
              )}
            </div>
            {isEditing && (
              <div className="picture-controls">
                <input type="file" id="profilePicture" accept="image/jpeg,image/jpg,image/png,image/webp" onChange={handleImageChange} style={{ display: 'none' }} />
                <label htmlFor="profilePicture" className="upload-button">
                  {imagePreview ? '🔄 Change Picture' : '📤 Upload Picture'}
                </label>
                {imagePreview && <button className="remove-picture-button" onClick={handleRemoveImage} type="button">🗑️ Remove Picture</button>}
              </div>
            )}
          </div>
        </div>

        <div className="partners-section">
          <div className="partner-card male">
            <div className="partner-icon">👨</div>
            <h3>Male Partner</h3>
            <div className="form-group">
              <label>Name <span className="required">*</span></label>
              {isEditing ? <input type="text" name="maleName" value={profileData.maleName} onChange={handleChange} placeholder="Enter name" required maxLength="100" /> : <p className="display-value">{profileData.maleName || 'Not set'}</p>}
            </div>
            <div className="form-group">
              <label>Date of Birth <span className="required">*</span></label>
              {isEditing ? (
                <input
                  type="date"
                  name="maleDob"
                  value={profileData.maleDob || ''}
                  onChange={handleDobChange}
                  min={minDob}
                  max={today}
                />
              ) : (
                <p className="display-value">{profileData.maleDob ? new Date(profileData.maleDob).toLocaleDateString() : 'Not set'}</p>
              )}
            </div>
            <div className="form-group">
              <label>Age </label>
              {isEditing ? (
                <input
                  type="number"
                  name="maleAge"
                  value={profileData.maleAge}
                  onChange={handleChange}
                  placeholder={profileData.maleDob ? 'Auto-filled from DOB' : 'Enter age'}
                  min="18"
                  max="120"
                  required
                  readOnly={!!profileData.maleDob}
                  className={profileData.maleDob ? 'age-autofilled' : ''}
                />
              ) : (
                <p className="display-value">{profileData.maleAge || 'Not set'}</p>
              )}
              {isEditing && profileData.maleDob && (
                <p className="dob-hint">Auto-calculated from date of birth</p>
              )}
            </div>
          </div>

          <div className="partner-card female">
            <div className="partner-icon">👩</div>
            <h3>Female Partner</h3>
            <div className="form-group">
              <label>Name <span className="required">*</span></label>
              {isEditing ? <input type="text" name="femaleName" value={profileData.femaleName} onChange={handleChange} placeholder="Enter name" required maxLength="100" /> : <p className="display-value">{profileData.femaleName || 'Not set'}</p>}
            </div>
            <div className="form-group">
              <label>Date of Birth <span className="required">*</span></label>
              {isEditing ? (
                <input
                  type="date"
                  name="femaleDob"
                  value={profileData.femaleDob || ''}
                  onChange={handleDobChange}
                  min={minDob}
                  max={today}
                />
              ) : (
                <p className="display-value">{profileData.femaleDob ? new Date(profileData.femaleDob).toLocaleDateString() : 'Not set'}</p>
              )}
            </div>
            <div className="form-group">
              <label>Age</label>
              {isEditing ? (
                <input
                  type="number"
                  name="femaleAge"
                  value={profileData.femaleAge}
                  onChange={handleChange}
                  placeholder={profileData.femaleDob ? 'Auto-filled from DOB' : 'Enter age'}
                  min="18"
                  max="120"
                  required
                  readOnly={!!profileData.femaleDob}
                  className={profileData.femaleDob ? 'age-autofilled' : ''}
                />
              ) : (
                <p className="display-value">{profileData.femaleAge || 'Not set'}</p>
              )}
              {isEditing && profileData.femaleDob && (
                <p className="dob-hint">Auto-calculated from date of birth</p>
              )}
            </div>
          </div>
        </div>

        <div className="description-section">
          <h3>Your Story <span className="optional-label">(Optional)</span></h3>
          <p className="section-subtitle">Tell us about your relationship journey</p>
          <div className="form-group">
            {isEditing ? (
              <textarea name="description" value={profileData.description} onChange={handleChange} placeholder="Share your story, how you met, what makes your relationship special..." rows="6" maxLength="2000" />
            ) : (
              <p className="display-value description-display">{profileData.description || 'No description provided yet'}</p>
            )}
          </div>

          {profileData.aiGeneratedDescription && !isEditing && (
            <div className="ai-description-display">
              <h4>✨ AI Generated Summary</h4>
              <p className="ai-description-text">{profileData.aiGeneratedDescription}</p>
            </div>
          )}
        </div>

        {isEditing && (
          <div className="required-notice">
            <span className="required">*</span> Required fields - Profile picture and description are optional
          </div>
        )}

        <div className="profile-actions">
          {isEditing ? (
            <>
              <button className="save-button" onClick={handleSave} disabled={saving}>
                {saving ? '⏳ Saving...' : '💾 Save Profile'}
              </button>
              {isProfileComplete() && (
                <button className="secondary-button" onClick={() => { setIsEditing(false); fetchProfile(); }} disabled={saving}>
                  Cancel
                </button>
              )}
            </>
          ) : (
            <>
              <button className="edit-button" onClick={() => setIsEditing(true)}>✏️ Edit Profile</button>
              {isProfileComplete() && (
                <button className="ai-button" onClick={handleGenerateAI} disabled={saving}>
                  {saving ? '⏳ Generating...' : '✨ Generate AI Description'}
                  {!subscription?.isPremium && <span className="premium-lock"> ⭐</span>}
                </button>
              )}
            </>
          )}
        </div>
      </div>

      {/* ✅ NEW: Danger Zone — Delete Account */}
      <div className="danger-zone">
        <h3 className="danger-zone-title">⚠️ Danger Zone</h3>
        <p className="danger-zone-text">
          Deleting your account is permanent. All your journal entries, profile data, subscriptions,
          and notifications will be permanently removed. This cannot be undone.
        </p>
        <button className="delete-account-button" onClick={handleOpenDeleteModal}>
          🗑️ Delete My Account
        </button>
      </div>

      {/* ✅ NEW: Delete confirmation modal */}
      {showDeleteModal && (
        <div className="delete-modal-overlay" onClick={handleCloseDeleteModal}>
          <div className="delete-modal" onClick={(e) => e.stopPropagation()}>
            <h2 className="delete-modal-title">⚠️ Delete Account</h2>
            <p className="delete-modal-text">
              This action is <strong>permanent</strong> and cannot be undone. All your journal entries,
              profile, subscription history, and notifications will be permanently deleted.
            </p>
            <p className="delete-modal-instruction">
              Type your username <strong>{username}</strong> below to confirm:
            </p>
            <input
              type="text"
              className="delete-modal-input"
              value={deleteConfirmText}
              onChange={(e) => setDeleteConfirmText(e.target.value)}
              placeholder="Type your username"
              disabled={deleting}
              autoFocus
            />
            {deleteError && <div className="delete-modal-error">⚠️ {deleteError}</div>}
            <div className="delete-modal-actions">
              <button className="delete-modal-cancel" onClick={handleCloseDeleteModal} disabled={deleting}>
                Cancel
              </button>
              <button
                className="delete-modal-confirm"
                onClick={handleConfirmDelete}
                disabled={deleting || deleteConfirmText.trim() !== username}
              >
                {deleting ? '⏳ Deleting...' : 'Permanently Delete My Account'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Profile;